import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { MultiSelect } from 'react-multi-select-component';
import { cloneDeep, isEmpty, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Page_Size, assetType as assetTypeOptions, checkArray, cloneData, createOptions, fileTypeOptions } from '../../Components/helper';
import * as action from './store/DAM.action';
import {
  getCategory3Details,
  getCategory4Details,
  getMultiSubBrandDetails,
  getSubBrandAccess,
  getCatBasedOnPT,
  getPCTypesDetails,
} from '../SystemMasters/Store/SystemMasters.action';
import * as todoCommonAction from '../Dashboard/TodoList/TodoCommon.action';

function DamFilter() {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const [isApply, setIsApply] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [state, setState] = useState({
    dropdownOptions: [],
    subBrandAccess: false,
  });
  const [category1LocalOptions, setCategory1LocalOptions] = useState([]);
  const [category2LocalOptions, setCategory2LocalOptions] = useState([]);
  const { dropdownOptions, subBrandAccess } = state;
  const reducer = useSelector((reducerstate) => reducerstate);
  const {
    ColorLibraryDetails,
    damModule: {
      damTagList,
      filterValues: {
        colourTheme,
        primaryColour,
        secondaryColour,
        folder,
        fileType,
        assetType,
        productType,
        brand,
        subBrand,
        gradingGroups,
        category1,
        category2,
        category3,
        category4,
        dynamicDropdown,
        tags,
      },
    },
    UserDetails: {
      userInfo: { userId },
    },
    damModule,
    SystemMastersDetails,
    PomDetails,
  } = reducer;
  const { ColorWaysLibList } = ColorLibraryDetails;
  const { allDamFolderList } = damModule;
  const { GradingList } = PomDetails;
  const { damAttributeDropdownList } = SystemMastersDetails;
  const { brandList, category1List, category2List, pcTypeList, category3List, category4List, multiSubBrandList } = SystemMastersDetails;
  // Fetch feature flag for cascading by Product Type
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);
  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(getSubBrandAccess());
      setState((prevState) => ({
        ...prevState,
        subBrandAccess: subBrandAccessRes?.data || false,
      }));
    };
    fetchSubBrandAccess();
  }, [dispatch]);
  // Ensure Product Types (Tier1) are available for the Product Type MultiSelect
  useEffect(() => {
    dispatch(
      getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);
  useEffect(() => {
    const callfn = async () => {
      await dispatch(action.fetchDamTag());
      await dispatch(action.fetchAllDAmFolders());
      const queryParams = {
        brandId: brand,
      };
      await dispatch(getMultiSubBrandDetails(queryParams));
    };
    callfn();
  }, [dispatch, brand]);

  const colourThemeOptions = useMemo(() => {
    if (checkArray(ColorWaysLibList)) return createOptions(ColorWaysLibList, 'name', '_id');
    return [];
  }, [ColorWaysLibList]);

  const folderOptions = useMemo(() => {
    if (checkArray(allDamFolderList)) return createOptions(allDamFolderList, 'name', '_id');
    return [];
  }, [allDamFolderList]);

  const productTypeOptions = useMemo(() => {
    if (checkArray(pcTypeList)) return createOptions(pcTypeList, 'name', '_id');
    return [];
  }, [pcTypeList]);

  const brandOptions = useMemo(() => {
    if (checkArray(brandList)) return createOptions(brandList, 'name', '_id');
    return [];
  }, [brandList]);

  const subBrandOptions = useMemo(() => {
    if (checkArray(multiSubBrandList)) return createOptions(multiSubBrandList, 'name', '_id');
    return [];
  }, [multiSubBrandList]);

  const gradingGroupsOptions = useMemo(() => {
    if (checkArray(GradingList)) return createOptions(GradingList, 'name', '_id');
    return [];
  }, [GradingList]);

  const category1Options = useMemo(() => {
    if (checkArray(category1LocalOptions)) return category1LocalOptions;
    if (checkArray(category1List)) return createOptions(category1List, 'name', '_id');
    return [];
  }, [category1LocalOptions, category1List]);

  const category2Options = useMemo(() => {
    if (checkArray(category2LocalOptions)) return category2LocalOptions;
    if (checkArray(category2List)) return createOptions(category2List, 'name', '_id');
    return [];
  }, [category2LocalOptions, category2List]);

  const category3Options = useMemo(() => {
    if (checkArray(category3List)) return createOptions(category3List, 'name', '_id');
    return [];
  }, [category3List]);

  const category4Options = useMemo(() => {
    if (checkArray(category4List)) return createOptions(category4List, 'name', '_id');
    return [];
  }, [category4List]);

  // Helpers and filtered options for cascading
  const getIds = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : []);
  const selectedPTIds = getIds(productType);
  const selectedCat1Ids = getIds(category1);
  const selectedCat2Ids = getIds(category2);
  const selectedCat3Ids = getIds(category3);

  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds.includes(id)) || pts?.some((id) => selectedPTIds.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    // Include Cat2 if linked via isUsedCat1 or catOneIds, or appears in Category3 with selected Cat1
    const cat2FromCat3 = new Set(
      (category3Options || [])
        .filter((c3) => selectedCat1Ids.includes(c3?.categoryOneId))
        .map((c3) => c3?.categoryTwoId)
        .filter(Boolean),
    );
    const filtered = base.filter((opt) => {
      const usedCat1 = [...(Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : []), ...(Array.isArray(opt?.catOneIds) ? opt.catOneIds : [])];
      const id = opt?._id;
      const mapsToCat1 = usedCat1?.some((cid) => selectedCat1Ids.includes(cid));
      const viaCat3 = cat2FromCat3.has(id);
      return mapsToCat1 || viaCat3;
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, category3Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => selectedCat1Ids.includes(opt?.categoryOneId) && selectedCat2Ids.includes(opt?.categoryTwoId));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter(
      (opt) => selectedCat1Ids.includes(opt?.categoryOneId) && selectedCat2Ids.includes(opt?.categoryTwoId) && selectedCat3Ids.includes(opt?.categoryThreeId),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  const tagsList = useMemo(() => {
    if (checkArray(damTagList)) return createOptions(damTagList, 'name', '_id');
    return [];
  }, [damTagList?.length]);
  // Fetch Category 1 and 2 without isActive filtering (include inactive)
  useEffect(() => {
    const fetchCats = async () => {
      if (!userId) return;
      try {
        const res1 = await dispatch(todoCommonAction.fetchCategoryAction({ accessedFrom: 'masters', categoryType: 1, loggedInUserId: userId }));
        if (Array.isArray(res1)) setCategory1LocalOptions(createOptions(res1, 'name', '_id'));

        const res2 = await dispatch(todoCommonAction.fetchCategoryAction({ accessedFrom: 'masters', categoryType: 2, loggedInUserId: userId }));
        if (Array.isArray(res2)) setCategory2LocalOptions(createOptions(res2, 'name', '_id'));
      } catch (e) {
        // ignore, fall back to store lists
      }
    };
    fetchCats();
  }, [dispatch, userId]);
  useEffect(() => {
    if (Array.isArray(damAttributeDropdownList) && damAttributeDropdownList.length > 0) {
      const copyState = cloneData(state);
      const cloneFolderOptions = cloneData(damAttributeDropdownList);
      cloneFolderOptions.forEach((label) => {
        label.options.forEach((option) => {
          option.label = option.keyName;
          option.value = option.keyName;
        });
      });
      set(copyState, 'dropdownOptions', cloneFolderOptions);
      setState((prevState) => ({
        ...prevState,
        ...copyState,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [damAttributeDropdownList]);

  useEffect(() => {
    if (checkArray(category1) && checkArray(category2) && userId) {
      dispatch(
        getCategory3Details({
          categoryOneId: category1,
          categoryTwoId: category2,
          categoryType: 3,
          loggedInUserId: userId,
        }),
      );
    }
  }, [category1, category2, dispatch, userId]);

  useEffect(() => {
    if (checkArray(category1) && checkArray(category2) && checkArray(category3) && userId) {
      dispatch(
        getCategory4Details({
          categoryOneId: category1,
          categoryTwoId: category2,
          categoryThreeId: category3,
          categoryType: 4,
          loggedInUserId: userId,
        }),
      );
    }
  }, [category1, category2, category3, dispatch, userId]);

  const handleInputChange = async (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);

    // Clear downstream categories when upstream changes
    if (name === 'category1' || name === 'category2') {
      obj.category3 = [];
      obj.category4 = [];
    } else if (name === 'category3') {
      obj.category4 = [];
    }

    // Preserve category selections on Product Type change; no hard clear needed
    // Filtered options already ensure invalid pills are hidden without wiping selections

    await dispatch(action.filterSortData(obj));
  };

  const handleFilterDynamicSelect = async (items, colId) => {
    const isColDataExist = dynamicDropdown.filter((e) => e.colId === colId);
    const copyState = cloneDeep(dynamicDropdown);
    if (checkArray(isColDataExist)) {
      isColDataExist.forEach((ele) => {
        copyState.splice(
          copyState.findIndex((e) => e.valId === ele.valId),
          1,
        );
      });
    }
    const value =
      checkArray(items) &&
      items.map((valId) => {
        return { colId, valId };
      });

    const obj = {};
    if (value) {
      set(obj, 'dynamicDropdown', [...copyState, ...value]);
    } else {
      set(obj, 'dynamicDropdown', [...copyState]);
    }
    await dispatch(action.filterSortData(obj));
  };

  const handleFilterApply = async () => {
    const obj = {
      pageIndex: 1,
      pageSize: Page_Size,
      loading: true,
      colourTheme,
      primaryColour,
      secondaryColour,
      folder,
      fileType,
      assetType,
      productType,
      brand,
      subBrand,
      gradingGroups,
      category1,
      category2,
      category3,
      category4,
      dynamicDropdown,
      tags,
    };

    let res = {};
    localStorage.setItem('damListFilter', JSON.stringify(obj));
    setIsApply(true);
    if (location?.state?.activeList === 'archived') {
      res = await dispatch(action.fetchDamAcrhivedFiles(obj));
    } else {
      res = await dispatch(action.fetchDamList(obj));
    }
    await dispatch(action.filterSortData(obj));
    if (res) {
      const cloneObj = {
        loading: false,
      };
      await dispatch(action.filterSortData(cloneObj));
    }
  };

  const handleFilterClear = async () => {
    setIsApply(false);
    const sortinObj = {
      pageIndex: 1,
      pageSize: Page_Size,
      loading: true,
      colourTheme: [],
      primaryColour: [],
      secondaryColour: [],
      folder: [],
      fileType: [],
      assetType: [],
      productType: [],
      brand: [],
      subBrand: [],
      gradingGroups: [],
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      dynamicDropdown: [],
      tags: [],
    };

    await dispatch(action.filterSortData(sortinObj));
    localStorage.removeItem('damListFilter');

    let res = {};

    if (location?.state?.activeList === 'archived') {
      res = await dispatch(action.fetchDamAcrhivedFiles(sortinObj));
    } else {
      res = await dispatch(action.fetchDamList(sortinObj));
    }
    if (res) {
      const obj = {
        loading: false,
      };
      await dispatch(action.filterSortData(obj));
    }
  };

  useEffect(() => {
    const localstorageFilter = JSON.parse(localStorage.getItem('damListFilter'));
    if (localstorageFilter) {
      for (const keys in localstorageFilter) {
        if (localstorageFilter[keys].length > 0) {
          setIsApply(true);
        }
      }
      dispatch(
        action.filterSortData({
          pageIndex: localstorageFilter?.pageIndex,
          pageSize: localstorageFilter?.pageSize,
          loading: localstorageFilter?.loading,
          colourTheme: localstorageFilter?.colourTheme,
          primaryColour: localstorageFilter?.primaryColour,
          secondaryColour: localstorageFilter?.secondaryColour,
          folder: localstorageFilter?.folder,
          fileType: localstorageFilter?.fileType,
          assetType: localstorageFilter?.assetType,
          productType: localstorageFilter?.productType,
          brand: localstorageFilter?.brand,
          subBrand: localstorageFilter?.subBrand,
          gradingGroups: localstorageFilter?.gradingGroups,
          category1: localstorageFilter?.category1,
          category2: localstorageFilter?.category2,
          category3: localstorageFilter?.category3,
          category4: localstorageFilter?.category4,
          dynamicDropdown: localstorageFilter?.dynamicDropdown,
          tags: localstorageFilter?.tags,
        }),
      );
    }
  }, []);

  return (
    <button type="button" title="Sort" className={isApply ? 'filter-btn filter-button_delete active' : 'filter-btn filter-button_delete'}>
      <OverlayTrigger
        placement="bottom"
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover popover-filter">
            <Popover.Content>
              <Row className="form-row">
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.FILE_Type')}</label>
                    <MultiSelect
                      name="group"
                      options={fileTypeOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select file type"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val.value),
                          'fileType',
                        )
                      }
                      value={fileTypeOptions.filter((val) => !isEmpty(fileType) && fileType.includes(val.value))}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.COLOR_THEME')}</label>
                    <MultiSelect
                      name="group"
                      options={colourThemeOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select Brand"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'colourTheme',
                        )
                      }
                      value={colourThemeOptions.filter((val) => !isEmpty(colourTheme) && colourTheme.includes(val._id))}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.PRIMARY_COLOUR')}</label>
                    <MultiSelect
                      name="group"
                      options={colourThemeOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select primary colour"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'primaryColour',
                        )
                      }
                      value={colourThemeOptions.filter((val) => !isEmpty(primaryColour) && primaryColour.includes(val._id))}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.SECONDARY_COLOUR')}</label>
                    <MultiSelect
                      name="group"
                      options={colourThemeOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select secondary colour"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'secondaryColour',
                        )
                      }
                      value={colourThemeOptions.filter((val) => !isEmpty(secondaryColour) && secondaryColour.includes(val._id))}
                    />
                  </FormGroup>
                </Col>

                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">Folder</label>
                    <MultiSelect
                      name="group"
                      options={folderOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select secondary colour"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'folder',
                        )
                      }
                      value={folderOptions.filter((val) => !isEmpty(folder) && folder.includes(val._id))}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.ASSET_TYPE')}</label>
                    <MultiSelect
                      name="group"
                      options={assetTypeOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select asset type"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val.value),
                          'assetType',
                        )
                      }
                      value={assetTypeOptions.filter((val) => !isEmpty(assetType) && assetType.includes(val.value))}
                    />
                  </FormGroup>
                </Col>

                {dropdownOptions?.length > 0 &&
                  dropdownOptions?.map(
                    (dropdown) =>
                      dropdown?.isActive && (
                        <Col md={3} key={dropdown?._id}>
                          <FormGroup>
                            <label className="select-label">{dropdown?.keyName}</label>
                            <MultiSelect
                              name="group"
                              options={dropdown?.options || []}
                              className="custom-multiselect w-100"
                              labelledBy="Select dropdown"
                              onChange={(selected) =>
                                handleFilterDynamicSelect(
                                  selected.map((val) => val._id),
                                  dropdown?._id,
                                )
                              }
                              value={dropdown.options.filter((val) => checkArray(dropdown.options) && dynamicDropdown.some((e) => e.valId === val._id))}
                            />
                          </FormGroup>
                        </Col>
                      ),
                  )}

                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.BRAND')}</label>
                    <MultiSelect
                      name="group"
                      options={brandOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select brand"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'brand',
                        )
                      }
                      value={brandOptions.filter((val) => !isEmpty(brand) && brand.includes(val._id))}
                    />
                  </FormGroup>
                </Col>
                {subBrandAccess && (
                  <Col md={3}>
                    <FormGroup>
                      <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.SUB_BRAND')}</label>
                      <MultiSelect
                        name="group"
                        options={subBrandOptions?.filter((val) => val?.isActive)}
                        className="custom-multiselect w-100"
                        labelledBy="Select brand"
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id),
                            'subBrand',
                          )
                        }
                        value={subBrandOptions.filter((val) => !isEmpty(subBrand) && subBrand.includes(val._id))}
                      />
                    </FormGroup>
                  </Col>
                )}

                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.Product_Type')}</label>
                    <MultiSelect
                      name="group"
                      options={productTypeOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select product type"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'productType',
                        )
                      }
                      value={productTypeOptions.filter((val) => !isEmpty(productType) && productType.includes(val._id))}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.CATEGORY_1')}</label>
                    <MultiSelect
                      name="group"
                      options={catBasedOnPTData ? category1Filtered : category1Options}
                      className="custom-multiselect w-100"
                      value={(catBasedOnPTData ? category1Filtered : category1Options).filter((val) => !isEmpty(category1) && category1.includes(val._id))}
                      labelledBy={t('FILTER.SELECT_CATEGORY_ONE')}
                      overrideStrings={{
                        selectSomeItems: t('FILTER.SELECT_CATEGORY_ONE'),
                      }}
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'category1',
                        )
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.CATEGORY_2')}</label>
                    <MultiSelect
                      name="group"
                      options={catBasedOnPTData ? category2Filtered : category2Options}
                      className="custom-multiselect w-100"
                      value={(catBasedOnPTData ? category2Filtered : category2Options).filter((val) => !isEmpty(category2) && category2.includes(val._id))}
                      labelledBy={t('FILTER.SELECT_CATEGORY_TWO')}
                      overrideStrings={{
                        selectSomeItems: t('FILTER.SELECT_CATEGORY_TWO'),
                      }}
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'category2',
                        )
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.CATEGORY_3')}</label>
                    <MultiSelect
                      name="group"
                      options={catBasedOnPTData ? category3Filtered : category3Options}
                      className="custom-multiselect w-100"
                      value={(catBasedOnPTData ? category3Filtered : category3Options).filter((val) => !isEmpty(category3) && category3.includes(val._id))}
                      labelledBy={t('FILTER.SELECT_CATEGORY_THREE')}
                      overrideStrings={{
                        selectSomeItems: t('FILTER.SELECT_CATEGORY_THREE'),
                      }}
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'category3',
                        )
                      }
                    />
                  </FormGroup>
                </Col>

                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.CATEGORY_4')}</label>
                    <MultiSelect
                      name="group"
                      options={catBasedOnPTData ? category4Filtered : category4Options}
                      className="custom-multiselect w-100"
                      value={(catBasedOnPTData ? category4Filtered : category4Options).filter((val) => !isEmpty(category4) && category4.includes(val._id))}
                      labelledBy={t('FILTER.SELECT_CATEGORY_FOUR')}
                      overrideStrings={{
                        selectSomeItems: t('FILTER.SELECT_CATEGORY_FOUR'),
                      }}
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'category4',
                        )
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.GRADING_GROUP')}</label>
                    <MultiSelect
                      name="group"
                      options={gradingGroupsOptions}
                      className="custom-multiselect w-100"
                      labelledBy="Select grading group"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val._id),
                          'gradingGroups',
                        )
                      }
                      value={gradingGroupsOptions.filter((val) => !isEmpty(gradingGroups) && gradingGroups.includes(val._id))}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <label className="select-label">{t('DAM_MAIN_VIEW.LISTING_VIEW.TAGS')}</label>
                    <MultiSelect
                      name="group"
                      options={tagsList}
                      className="custom-multiselect w-100"
                      labelledBy="Select grading group"
                      onChange={(selected) =>
                        handleInputChange(
                          selected.map((val) => val.name),
                          'tags',
                        )
                      }
                      value={tagsList.filter((val) => !isEmpty(tags) && tags.includes(val.name))}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <div className="d-flex justify-content-end">
                    <button onClick={() => handleFilterApply()} className="btn btn-default mr-2" type="button">
                      {t('DAM_MAIN_VIEW.LISTING_VIEW.APPLY')}
                    </button>
                    <button onClick={() => handleFilterClear()} className="btn btn-default" type="button">
                      {t('DAM_MAIN_VIEW.LISTING_VIEW.CLEAR')}
                    </button>
                  </div>
                </Col>
              </Row>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-filter" />
      </OverlayTrigger>
    </button>
  );
}

export default DamFilter;
